"use client"

import { useEffect, useMemo, useState } from "react"
import { BellOff, ChevronLeft, ChevronRight, ExternalLink, Loader2, MessageSquare, RefreshCw, Search, Settings, X } from "lucide-react"

import { MessagesInboxFilterButton, MessagesInboxFilterPills } from "@/components/messages-inbox-filters"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useAuth } from "@/contexts/auth-context"
import { useInboxConversations, type InboxConversation } from "@/hooks/use-inbox-conversations"
import { useMessageReadActions } from "@/hooks/use-message-read-actions"
import { useWorkflowRowNavigation } from "@/hooks/use-workflow-row-navigation"
import { useTranslation } from "@/hooks/use-translation"
import {
  EMPTY_ATTRIBUTE_FILTERS,
  type InboxAttributeFilters,
  collectFilterOptions,
  hasActiveAttributeFilters,
  matchesAttributeFilters,
  matchesWorkOrderNumber,
  needsMetadata,
} from "@/lib/inbox-filters"

// Combinable filters (AND). Empty selection = show everything. All read from the loaded set — unread /
// important / read / muted from the my-states index, client / internal from the background summaries pass
// (the split) — so they cover every conversation, not just the visible page.
type InboxFilter = "unread" | "client" | "internal" | "important" | "read" | "muted"
// Display order. Read sits next to Unread; client/internal are sub-filters of Unread (they qualify which
// audience is unread) and only render while Unread is active — see the filter row below.
const FILTER_KEYS: InboxFilter[] = ["unread", "read", "client", "internal", "important", "muted"]
const UNREAD_SUBFILTERS: InboxFilter[] = ["client", "internal"]

const PAGE_SIZE = 25

interface MessagesInboxModalProps {
  isOpen: boolean
  onClose: () => void
  onOpenSettings: () => void
  /** Open this conversation's chat in the side panel (rendered by the hub, OUTSIDE this modal). */
  onOpenChat: (row: InboxConversation) => void
  /** Called after a WO/Assignment redirect is set up — the hub switches to the WO grid tab + closes. */
  onAfterNavigate: () => void
  /** Request-tab handoff for the redirect (Phase 2 entity types); Assignment rows never use it. */
  onNavigateToRequestTab?: (documentType: string, item: Record<string, any>) => void
}

function matchesFilters(row: InboxConversation, active: Set<InboxFilter>): boolean {
  for (const f of active) {
    if (f === "unread" && !(row.unread > 0)) return false
    if (f === "client" && !(row.clientUnread > 0)) return false
    if (f === "internal" && !(row.internalUnread > 0)) return false
    if (f === "important" && !row.hasImportant) return false
    if (f === "read" && !(row.unread === 0)) return false
    if (f === "muted" && !row.isMuted) return false
  }
  return true
}

// Row order: muted last, then important-unread, then unread, then most recent. Last-activity comes from
// the background summaries pass (global), so the whole set sorts by recency — not just the visible page.
// Before summaries land, lastActivity is null and rows fall back to newest-record-first (entity id), then
// the list settles into recency order once the previews arrive.
function compareRows(a: InboxConversation, b: InboxConversation): number {
  if (a.isMuted !== b.isMuted) return a.isMuted ? 1 : -1
  if (a.hasImportant !== b.hasImportant) return a.hasImportant ? -1 : 1
  const au = a.unread > 0, bu = b.unread > 0
  if (au !== bu) return au ? -1 : 1
  const ta = a.lastActivity ? Date.parse(a.lastActivity) : -1
  const tb = b.lastActivity ? Date.parse(b.lastActivity) : -1
  if (ta !== tb) return tb - ta
  const an = Number(a.entityId), bn = Number(b.entityId)
  if (!Number.isNaN(an) && !Number.isNaN(bn)) return bn - an
  return b.entityId.localeCompare(a.entityId)
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  return (parts[0][0] + (parts[1]?.[0] ?? "")).toUpperCase()
}

// Show a long id (GUID) compactly; the full value lives in a hover tooltip.
function truncateId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 8)}…` : id
}

// Messaging-app timestamp: time today, weekday within a week, else short date.
function formatWhen(iso: string | null): string {
  if (!iso) return ""
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ""
  const now = new Date()
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
  if (now.getTime() - d.getTime() < 7 * 86400000) return d.toLocaleDateString(undefined, { weekday: "short" })
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

export function MessagesInboxModal({
  isOpen,
  onClose,
  onOpenSettings,
  onOpenChat,
  onAfterNavigate,
  onNavigateToRequestTab,
}: MessagesInboxModalProps) {
  // Loose-typed t: some inbox labels are not in the TranslationKey union yet — add real keys when this
  // graduates from behind the messages flag. Each call falls back to the English literal.
  const { t: tKey } = useTranslation()
  const t = tKey as unknown as (key: string) => string | undefined
  const { isInternalUser } = useAuth()
  const [search, setSearch] = useState("")
  // Date / stage / sender / client filters (lib/inbox-filters). Deliberately NOT reset on reopen: the modal
  // stays mounted, so they persist while the page is open, like the status chips.
  const [attrs, setAttrs] = useState<InboxAttributeFilters>(EMPTY_ATTRIBUTE_FILTERS)
  const term = search.trim()
  // Filters popover open: its Stage / Client options come from the enriched rows, so they'd only cover pages
  // already visited without the full-set metadata.
  const [filtersOpen, setFiltersOpen] = useState(false)
  // Stage / client / WO # need metadata for EVERY row, so only then does the hook search the whole set in the
  // background — a plain open or refresh stays at the page-level search.
  const needAllMetadata = filtersOpen || needsMetadata(attrs) || term.length > 0
  const { rows, isLoading, enrich, searchCandidateIds, refresh, metadataComplete } = useInboxConversations(isOpen, needAllMetadata)
  const { navigateToRow } = useWorkflowRowNavigation()
  // entityType arg is irrelevant here — inbox rows carry their own {entityTypeId, entityId}, which
  // resolveMessageConversationKey trusts directly, so mark/mute group correctly across mixed types. The
  // cast to Record<string, any>[] matches worker-search-modal (a named interface doesn't structurally
  // satisfy the hook's index-signature param without it).
  const { markMessages, muteMessages } = useMessageReadActions("Assignment", rows as unknown as Record<string, any>[])

  const [active, setActive] = useState<Set<InboxFilter>>(new Set<InboxFilter>(["unread"]))
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  // Worker-name search: matching entity-id VALUES from the server (null = no active search). Intersected
  // with the loaded rows below so only the user's own conversations show.
  const [matchedIds, setMatchedIds] = useState<Set<string> | null>(null)
  const clearAll = () => { setAttrs(EMPTY_ATTRIBUTE_FILTERS); setSearch("") }
  // Popovers portal into a div inside DialogContent so they work inside the dialog's focus trap.
  const [popoverContainer, setPopoverContainer] = useState<HTMLDivElement | null>(null)

  // Reset to page 1 AND clear the selection when the filter or search changes: select-all can span the
  // whole filtered set, and a selection carried across a filter switch would let a bulk action silently
  // act on rows that are no longer visible.
  // `term` too, not just matchedIds: the local WO # match changes the visible rows immediately, before the
  // debounced name lookup updates matchedIds.
  useEffect(() => { setPage(1); setSelected(new Set()) }, [active, matchedIds, attrs, term])
  useEffect(() => {
    if (isOpen) {
      setPage(1)
      setSelected(new Set())
      setSearch("")
      setMatchedIds(null)
      // The modal stays mounted, so a popover left open when the dialog closed would pop open again (and keep
      // the full-set search on) on the next open.
      setFiltersOpen(false)
    }
  }, [isOpen])

  // Debounced worker-name lookup — resolves the term to matching entity ids server-side.
  useEffect(() => {
    if (!isOpen) return
    const q = search.trim()
    if (!q) { setMatchedIds(null); return }
    let cancelled = false
    const handle = setTimeout(() => {
      void searchCandidateIds(q).then((ids) => { if (!cancelled) setMatchedIds(ids) })
    }, 300)
    return () => { cancelled = true; clearTimeout(handle) }
  }, [search, isOpen, searchCandidateIds])

  // Filtering + sorting run over the WHOLE loaded set, not the visible page. Order: search, then the
  // attribute filters, then the status chips — so the chip counts, Showing, Select all and paging all
  // reflect the search and attribute filters.
  // Name matches come from the server (matchedIds); WO # matches are local over the enriched rows.
  // While the name lookup is pending (matchedIds null) the search does not restrict, as before.
  const searched = useMemo(
    () => (matchedIds ? rows.filter((r) => matchedIds.has(r.entityId) || matchesWorkOrderNumber(r, term)) : rows),
    [rows, matchedIds, term]
  )
  const scoped = useMemo(() => searched.filter((r) => matchesAttributeFilters(r, attrs)), [searched, attrs])
  const visible = useMemo(
    () => scoped.filter((r) => matchesFilters(r, active)).sort(compareRows),
    [scoped, active]
  )
  // Chip counts reflect what's in scope (the search + attribute-filter subset).
  const filterCount = useMemo(() => {
    const one = (f: InboxFilter) => scoped.filter((r) => matchesFilters(r, new Set([f]))).length
    return Object.fromEntries(FILTER_KEYS.map((f) => [f, one(f)])) as Record<InboxFilter, number>
  }, [scoped])
  // Options from ALL loaded rows, so they don't disappear while filtering.
  const options = useMemo(() => collectFilterOptions(rows), [rows])
  // Filters that exclude rows still missing metadata — drives the Loading empty state. The popover's own
  // Loading hint just follows metadataComplete (its options fill in as the rows do).
  const metadataPending = (needsMetadata(attrs) || term.length > 0) && !metadataComplete

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE))
  const safePage = Math.min(page, pageCount)
  const paged = useMemo(
    () => visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [visible, safePage]
  )

  // Enrich the rows on screen; the hook enriches the rest in the background only while needAllMetadata (see
  // use-inbox-conversations).
  useEffect(() => {
    if (isOpen && paged.length > 0) void enrich(paged)
  }, [isOpen, paged, enrich])

  // unread and read are opposite states, so they're mutually exclusive — selecting one drops the other
  // (otherwise "unread AND read" ANDs to nothing). The rest stay freely combinable.
  const EXCLUSIVE: Partial<Record<InboxFilter, InboxFilter>> = { unread: "read", read: "unread" }
  const toggleFilter = (f: InboxFilter) =>
    setActive((prev) => {
      const next = new Set(prev)
      if (next.has(f)) {
        next.delete(f)
      } else {
        next.add(f)
        const opposite = EXCLUSIVE[f]
        if (opposite) next.delete(opposite)
      }
      // client/internal only qualify unread — drop them whenever unread isn't active.
      if (!next.has("unread")) UNREAD_SUBFILTERS.forEach((sf) => next.delete(sf))
      return next
    })

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })

  const selectedRows = useMemo(() => rows.filter((r) => selected.has(r.id)), [rows, selected])

  // Two ways to bulk-select: the current page, or the whole filtered set (which can be thousands —
  // the bulk mark/mute hook already chunks each call to the endpoint's per-request cap, so selecting
  // everything is safe). allIds spans every filtered conversation, not just the visible page.
  const pageIds = useMemo(() => paged.map((r) => r.id), [paged])
  const allIds = useMemo(() => visible.map((r) => r.id), [visible])
  const allSelectedOnPage = pageIds.length > 0 && pageIds.every((id) => selected.has(id))
  const someSelectedOnPage = pageIds.some((id) => selected.has(id))
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))

  const toggleSelectPage = () =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (allSelectedOnPage) pageIds.forEach((id) => next.delete(id))
      else pageIds.forEach((id) => next.add(id))
      return next
    })

  const toggleSelectAll = () => setSelected(() => (allSelected ? new Set<string>() : new Set(allIds)))

  const runBulk = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
      await refresh()
      setSelected(new Set())
    } finally {
      setBusy(false)
    }
  }

  const goToRecord = (row: InboxConversation) =>
    navigateToRow(row as unknown as Record<string, any>, { onNavigate: onAfterNavigate, onNavigateToRequestTab })

  // Client users never see internal messages, so the client/internal audience split is meaningless to
  // them — a conversation is simply read or unread. Internal users keep both sub-filters.
  const availableFilterKeys = isInternalUser ? FILTER_KEYS : FILTER_KEYS.filter((f) => !UNREAD_SUBFILTERS.includes(f))

  const FILTER_LABELS: Record<InboxFilter, string> = {
    unread: t("unread") || "Unread",
    client: t("client") || "Client",
    internal: t("internal") || "Internal",
    important: t("important") || "Important",
    read: t("read") || "Read",
    muted: t("muted") || "Muted",
  }

  return (
    // Standard modal: overlay + click-outside-close. The chat opens as its OWN modal layer above this one
    // (rendered by the hub), so the inbox stays mounted underneath and becomes interactive again the
    // moment the chat closes.
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose() }}>
      {/* FIXED height (flex column, list takes the rest), so a row appearing — the filter pills — shrinks the
          list instead of resizing the modal. NOT overflow-hidden: the filter popover and its calendars portal
          into this dialog (for its focus trap) and are wider than the room beside the Filters button, so
          clipping here cut them off. The list scrolls inside its own ScrollArea. */}
      <DialogContent className="flex h-[85vh] max-w-3xl flex-col gap-0 p-0">
        <div ref={setPopoverContainer} />
        <DialogHeader className="flex shrink-0 flex-row items-center justify-between space-y-0 border-b px-5 py-4">
          <DialogTitle className="text-base font-semibold">{t("messages") || "Messages"}</DialogTitle>
        </DialogHeader>

        {/* Worker-name or WO # search — name matching is resolved server-side across the WorkOrder + requests
            indexes; WO # matching is local over the enriched rows. */}
        <div className="flex shrink-0 items-center gap-2 border-b px-5 py-2">
          <div className="relative flex-1">
            <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={tKey("inboxSearchPlaceholder")}
              className="h-8 pl-8 pr-8 text-sm"
            />
            {search && (
              <button
                type="button"
                aria-label={t("clearSearch") || "Clear search"}
                onClick={() => setSearch("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <MessagesInboxFilterButton
            filters={attrs}
            onChange={setAttrs}
            options={options}
            showClientFilter={isInternalUser}
            optionsLoading={!metadataComplete}
            open={filtersOpen}
            onOpenChange={setFiltersOpen}
            portalContainer={popoverContainer}
          />
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => void refresh()} aria-label={t("refresh") || "Refresh"}>
            <RefreshCw size={16} className={isLoading ? "animate-spin" : ""} />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onOpenSettings} aria-label={t("messageNotifications") || "Notification settings"}>
            <Settings size={16} />
          </Button>
        </div>

        <MessagesInboxFilterPills filters={attrs} onChange={setAttrs} onClearAll={clearAll} />

        {/* Combinable filters (AND). Selecting none shows everything. client/internal render only while
            Unread is active — they qualify which audience is unread. */}
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-b px-5 py-2">
          {availableFilterKeys.filter((f) => !UNREAD_SUBFILTERS.includes(f) || active.has("unread")).map((f) => (
            <Button
              key={f}
              variant={active.has(f) ? "elliptical" : "elliptical-outline"}
              className="h-8 min-h-0 px-3 text-xs"
              onClick={() => toggleFilter(f)}
            >
              {FILTER_LABELS[f]} <span className="ml-1 opacity-60">{filterCount[f]}</span>
            </Button>
          ))}
          {active.size > 0 && (
            <Button variant="ghost" className="h-8 min-h-0 px-2 text-xs text-muted-foreground" onClick={() => setActive(new Set())}>
              {t("showAll") || "Show all"}
            </Button>
          )}
        </div>

        {/* Status + bulk-select bar — FIXED height (h-11) so selecting/deselecting never shifts the layout
            (the mass-action buttons are taller than the idle text). The page checkbox aligns with the per-row
            checkboxes below (px-4); select-all sits right beside it, spanning the whole filtered set; the mass
            actions appear to their right when rows are checked. */}
        <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-slate-50 px-4 text-xs">
          {paged.length > 0 && (
            <Checkbox
              checked={allSelectedOnPage ? true : someSelectedOnPage ? "indeterminate" : false}
              onCheckedChange={toggleSelectPage}
              aria-label={t("selectPage") || "Select page"}
            />
          )}
          <span className={selected.size > 0 ? "font-medium" : "text-muted-foreground"}>
            {selected.size > 0
              ? `${selected.size} ${t("selected") || "selected"}`
              : `${t("showing") || "Showing"} (${paged.length})`}
          </span>
          {paged.length > 0 && (
            <label className="ml-1 flex cursor-pointer items-center gap-1.5 text-muted-foreground">
              <Checkbox
                checked={allSelected}
                onCheckedChange={toggleSelectAll}
                aria-label={t("selectAll") || "Select all"}
              />
              {t("selectAll") || "Select all"} <span className="opacity-60">({visible.length})</span>
            </label>
          )}
          {selected.size > 0 && (
            <>
              <Button variant="elliptical-outline" className="ml-1 h-7 min-h-0 text-xs" disabled={busy} onClick={() => void runBulk(() => markMessages(selectedRows as unknown as Record<string, any>[], true))}>
                {t("markRead") || "Mark read"}
              </Button>
              <Button variant="elliptical-outline" className="h-7 min-h-0 text-xs" disabled={busy} onClick={() => void runBulk(() => markMessages(selectedRows as unknown as Record<string, any>[], false))}>
                {t("markUnread") || "Mark unread"}
              </Button>
              <Button variant="elliptical-outline" className="h-7 min-h-0 text-xs" disabled={busy} onClick={() => void runBulk(() => muteMessages(selectedRows as unknown as Record<string, any>[], false))}>
                {t("mute") || "Mute"}
              </Button>
              <Button variant="elliptical-outline" className="h-7 min-h-0 text-xs" disabled={busy} onClick={() => void runBulk(() => muteMessages(selectedRows as unknown as Record<string, any>[], true))}>
                {t("unmute") || "Unmute"}
              </Button>
              <Button variant="ghost" className="h-7 min-h-0 px-2 text-xs" onClick={() => setSelected(new Set())}>
                {t("clear") || "Clear"}
              </Button>
            </>
          )}
        </div>

        {/* Force Radix's viewport wrapper to block: it defaults to display:table + min-width:100%, which
            shrink-wraps to the widest row (a long, nowrap message), growing past the modal and defeating the
            rows' min-w-0/truncate chain. block keeps it at 100% so the message column can truncate. */}
        <ScrollArea className="min-h-0 flex-1 [&>[data-radix-scroll-area-viewport]>div]:!block">
          {isLoading && rows.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
              <Loader2 size={16} className="mr-2 animate-spin" /> {t("loading") || "Loading…"}
            </div>
          ) : paged.length === 0 ? (
            // Metadata still arriving → "no match" would be premature. rows.length > 0 keeps the default
            // Unread tab on "No conversations to show." for a user with no conversations at all.
            metadataPending ? (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                <Loader2 size={16} className="mr-2 animate-spin" /> {t("loading") || "Loading…"}
              </div>
            ) : rows.length > 0 && (hasActiveAttributeFilters(attrs) || term || active.size > 0) ? (
              <div className="flex h-40 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
                {tKey("inboxNoMatches")}
                {(hasActiveAttributeFilters(attrs) || term) && (
                  <Button variant="elliptical-outline" className="h-8 min-h-0 px-3 text-xs" onClick={clearAll}>
                    {tKey("clearFilters")}
                  </Button>
                )}
              </div>
            ) : (
              <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
                {t("noConversations") || "No conversations to show."}
              </div>
            )
          ) : (
            <ul className="divide-y">
              {paged.map((row) => {
                // Preview comes from the background summaries pass (messageCount is known immediately from
                // the index, so we can show "no messages yet" without waiting).
                const hasMessages = row.messageCount > 0
                const sender = hasMessages ? row.previewAuthor : (t("noMessagesYet") || "No messages yet")
                const message = hasMessages ? row.previewSnippet : ""
                const title = row.workerFullName || (row.isRequestRow ? (row.stage || (t("request") || "Request")) : `#${row.fboWorkOrderNumber || row.entityId}`)
                // Client-request family shows the request document's GUID (truncated, full on hover) instead
                // of a reference number; other request types keep their family reference (#…).
                const isClientReqFamily = ["ClientRequest", "Request", "OnboardingRequest"].includes(row.entityTypeId)
                const docGuid = isClientReqFamily ? (row.requestId || row.entityId) : ""
                const subtitle = row.isRequestRow
                  ? [row.stage, !isClientReqFamily && row.fboWorkOrderNumber && `#${row.fboWorkOrderNumber}`].filter(Boolean).join(" · ")
                  : [row.fboWorkOrderNumber && `WO ${row.fboWorkOrderNumber}`, row.stage].filter(Boolean).join(" · ")
                const audienceTitle = [
                  row.clientUnread > 0 ? `${row.clientUnread} ${t("client") || "client"}` : null,
                  row.internalUnread > 0 ? `${row.internalUnread} ${t("internal") || "internal"}` : null,
                ].filter(Boolean).join(", ")
                return (
                  <li key={row.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                    <Checkbox
                      checked={selected.has(row.id)}
                      onCheckedChange={() => toggleSelect(row.id)}
                      aria-label={t("select") || "Select"}
                      onClick={(e) => e.stopPropagation()}
                    />

                    {/* Clickable body — redirects to the record. */}
                    <button
                      type="button"
                      className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      onClick={() => goToRecord(row)}
                    >
                      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-600">
                        {initials(row.workerFullName || row.clientName || (row.isRequestRow ? row.stage : row.entityId))}
                      </span>

                      <span className="flex w-40 flex-none flex-col">
                        <span className="flex items-center gap-1 truncate text-sm font-medium">
                          {title}
                          {row.isMuted && <BellOff size={12} className="flex-none text-muted-foreground" />}
                        </span>
                        <span className="truncate text-[11px] text-muted-foreground">
                          {subtitle}
                          {docGuid && (
                            <>
                              {subtitle && " · "}
                              <span title={docGuid} className="cursor-help underline decoration-dotted underline-offset-2">
                                {truncateId(docGuid)}
                              </span>
                            </>
                          )}
                        </span>
                      </span>

                      <span className="h-8 w-px flex-none bg-border" />

                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-xs font-medium" title={sender || undefined}>{sender}</span>
                        <span className="truncate text-xs text-muted-foreground" title={message || undefined}>{message}</span>
                      </span>

                      {/* Time column — the last-activity time on its own, out of the message so it reads clean. */}
                      <span className="w-14 flex-none whitespace-nowrap text-right text-[10px] text-muted-foreground">
                        {row.lastActivity ? formatWhen(row.lastActivity) : ""}
                      </span>

                      {/* Count column — badge only, centered. The client/internal split is a tooltip so it
                          never crowds the message preview. */}
                      <span className="flex w-8 flex-none items-center justify-end">
                        {row.unread > 0 && (
                          <span
                            title={audienceTitle || undefined}
                            className={`flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold text-white ${row.hasImportant ? "bg-[#E5484D]" : "bg-[#0F214B]"}`}
                          >
                            {row.unread > 99 ? "99+" : row.unread}
                          </span>
                        )}
                      </span>
                    </button>

                    {/* Actions — do not trigger the row redirect. */}
                    <div className="flex flex-none items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("openChat") || "Open chat"} onClick={() => onOpenChat(row)}>
                        <MessageSquare size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={t("goToEntity") || "Go to record"}
                        onClick={() => goToRecord(row)}
                      >
                        <ExternalLink size={15} />
                      </Button>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </ScrollArea>

        {/* Footer is ALWAYS rendered (even for a single page / no results) so the modal height never jumps
            as the result count changes. */}
        <div className="flex shrink-0 items-center justify-between border-t px-5 py-2 text-xs text-muted-foreground">
          <span>
            {visible.length === 0
              ? "0"
              : `${(safePage - 1) * PAGE_SIZE + 1}–${Math.min(safePage * PAGE_SIZE, visible.length)} ${t("of") || "of"} ${visible.length}`}
          </span>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={safePage <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} aria-label={t("previous") || "Previous"}>
              <ChevronLeft size={16} />
            </Button>
            <Button variant="ghost" size="icon" className="h-7 w-7" disabled={safePage >= pageCount} onClick={() => setPage((p) => Math.min(pageCount, p + 1))} aria-label={t("next") || "Next"}>
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
